import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, PlugZap, WifiOff, Wifi, Play, RotateCcw, XCircle, Ban, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PairDialog, RevokeDialog } from "./PairDevice";
import { SetupGuide } from "./SetupGuide";

const CONN = {
  not_configured: { label: "Kein Produktions-PC eingerichtet", icon: PlugZap, cls: "border-white/15 text-gray-300",
    text: "Es ist noch kein Produktions-PC gekoppelt. Die Online-App führt keine Produktionsarbeit selbst aus; Aufträge warten, bis ein gekoppelter PC sie abholt." },
  offline: { label: "PC offline", icon: WifiOff, cls: "border-red-400/40 text-red-300",
    text: "Ein PC ist gekoppelt, meldet sich aber nicht. Aufträge bleiben in der Warteschlange, bis er wieder online ist oder sie ablaufen." },
  connected: { label: "Verbunden", icon: Wifi, cls: "border-[#A3E635]/50 text-[#A3E635]",
    text: "Der PC holt Aufträge per ausgehender HTTPS-Verbindung ab." },
};
const JOB = {
  queued: ["Auftrag wartet", "text-amber-300"], running: ["Läuft", "text-sky-300"],
  succeeded: ["Ergebnis bereit", "text-[#A3E635]"], failed: ["Fehlgeschlagen", "text-red-300"],
  expired: ["Abgelaufen", "text-red-300"], cancelled: ["Abgebrochen", "text-gray-400"],
};

export const fmtZurich = (v, lang) => (v ? new Date(v).toLocaleString(lang === "en" ? "en-GB" : "de-CH",
  { dateStyle: "short", timeStyle: "medium", timeZone: "Europe/Zurich" }) : "—");

function jobLabel(j) {
  if (j.status === "cancelled" && j.runner_id) return ["Storno angefordert · Ergebnis verworfen", "text-gray-400"];
  return JOB[j.status] || [j.status, "text-gray-300"];
}

function JobBinding({ j, runners }) {
  const { t } = useI18n();
  const bound = j.bound_runner_id && runners.find(r => r.runner_id === j.bound_runner_id);
  const leaseGone = j.status === "running" && j.lease_until && new Date(j.lease_until) <= new Date();
  return (
    <>
      {j.bound_runner_id && <p className="text-xs text-gray-400 mt-2" data-testid="pc-job-bound">{t("Gebunden an Gerät")}: {bound?.label || j.bound_runner_id}</p>}
      {leaseGone && <p className="text-xs text-amber-300 mt-1" data-testid="pc-job-lease-expired">{t("Keine Rückmeldung des PCs (Lease abgelaufen). Der Auftrag wartet auf dasselbe Gerät und wird nicht automatisch an ein anderes Gerät gegeben.")}</p>}
      {j.reassignment_required && <p className="text-xs text-red-300 mt-1" data-testid="pc-job-reassign">{t("Bewusste Neuzuordnung nötig: Das gebundene Gerät ist gesperrt oder abgelaufen. Auf dem alten PC kann lokale Arbeit weiterlaufen; eine Neuzuordnung kann doppelte Arbeit erzeugen.")}</p>}
    </>
  );
}

function JobRow({ j, onAction, runners }) {
  const { t, lang } = useI18n();
  const [label, cls] = jobLabel(j);
  return (
    <li className="rounded-xl border border-white/10 p-4 min-w-0" data-testid={`pc-job-${j.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {j.is_test && <span className="rounded bg-amber-400/15 text-amber-300 text-[10px] font-bold px-1.5 py-0.5">TEST</span>}
        <span className="font-mono text-xs text-gray-300 break-all">{j.action}</span>
        <span className={`text-sm font-semibold ${cls}`} data-testid="pc-job-status">{t(label)}</span>
        <span className="text-xs text-gray-500 sm:ml-auto">{fmtZurich(j.updated_at, lang)} · {t("Versuch")} {j.attempts}</span>
      </div>
      {j.error && <p className="text-xs text-red-300 mt-2 break-words" data-testid="pc-job-error">{j.error}</p>}
      {j.result?.local_still_running && (
        <p className="text-xs text-amber-300 mt-2" data-testid="pc-job-local-running">
          {t("Lokale Arbeit läuft auf dem PC weiter (lokaler Auftrag {id}). Dieses Ergebnis ist verworfen; «Wiederholen» gibt den Auftrag nur an dasselbe Gerät zurück.").replace("{id}", j.result.local_job_id || "—")}</p>)}
      <JobBinding j={j} runners={runners} />
      {j.status === "succeeded" && j.result && (
        <pre className="text-[11px] text-gray-300 bg-black/40 rounded-lg p-2 mt-2 overflow-x-auto" data-testid="pc-job-result">{JSON.stringify(j.result, null, 1)}</pre>
      )}
      <div className="flex gap-2 mt-2">
        {["queued", "running"].includes(j.status) && !j.cancel_requested && (
          <button onClick={() => onAction(j.id, "cancel")} data-testid="pc-job-cancel" className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {t("Abbrechen")}</button>)}
        {j.cancel_requested && j.status === "running" && <span className="text-xs text-gray-400" data-testid="pc-job-cancel-requested">{t("Storno angefordert – der PC beendet nur das Warten, lokale Arbeit kann weiterlaufen")}</span>}
        {["failed", "expired", "cancelled"].includes(j.status) && (
          <button onClick={() => onAction(j.id, "retry")} data-testid="pc-job-retry" className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> {t("Wiederholen")}</button>)}
      </div>
    </li>
  );
}

function runnerState(r) {
  if (r.status === "revoked") return "gesperrt";
  if (r.expires_at && new Date(r.expires_at) <= new Date()) return "abgelaufen";
  return "aktiv";
}

function Runners({ runners, onPair, onRevoke }) {
  const { t, lang } = useI18n();
  return (
    <section className="rounded-2xl border border-white/10 p-5" data-testid="pc-runners">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h2 className="font-bold">{t("Gekoppelte Geräte")}</h2>
        <button onClick={onPair} data-testid="pc-pair-open" className="sm:ml-auto bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-3 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> {t("Gerät koppeln")}</button>
      </div>
      {runners.length === 0 ? <p className="text-sm text-gray-400">{t("Keine.")}</p> : (
        <ul className="space-y-2 text-sm">{runners.map(r => {
          const st = runnerState(r);
          return (
            <li key={r.runner_id} className="flex flex-wrap items-center gap-2 break-all" data-testid={`pc-runner-${r.runner_id}`}>
              {r.environment === "test" && <span className="rounded bg-amber-400/15 text-amber-300 text-[10px] font-bold px-1.5 py-0.5">TEST</span>}
              <span>{r.label}</span>
              <span className={st === "aktiv" ? "text-[#A3E635]" : "text-red-300"} data-testid="pc-runner-state">{t(st)}</span>
              <span className="text-gray-500 text-xs">{r.version || "—"} · {t("zuletzt")} {fmtZurich(r.last_seen_at, lang)} · {t("gültig bis")} {fmtZurich(r.expires_at, lang)}</span>
              {st !== "gesperrt" && <button onClick={() => onRevoke(r)} data-testid="pc-runner-revoke" className="sm:ml-auto text-xs text-red-300 hover:text-red-200 inline-flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> {t("Sperren")}</button>}
            </li>);
        })}</ul>)}
    </section>
  );
}

export default function PCProduction() {
  const { t, lang } = useI18n();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [pairing, setPairing] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const load = useCallback(() => api.get("/admin/pc-relay/status").then(r => { setD(r.data); setErr(null); })
    .catch(e => setErr(e?.response?.data?.detail || "Status nicht abrufbar")), []);
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);
  const testJob = () => api.post("/admin/pc-relay/jobs", { action: "selftest.ping", environment: "test",
    params: { nonce: `test-${Date.now()}` } }).then(() => { toast.success(t("TEST-Auftrag erstellt")); load(); })
    .catch(e => toast.error(t(e?.response?.data?.detail || "Fehler")));
  const onAction = (id, a) => api.post(`/admin/pc-relay/jobs/${id}/${a}`).then(load)
    .catch(e => toast.error(t(e?.response?.data?.detail || "Fehler")));
  if (err) return <p className="text-red-300 text-sm" data-testid="pc-error">{t(err)}</p>;
  if (!d) return null;
  const c = CONN[d.connection.state];
  const Icon = c.icon;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="pc-production">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Cpu className="w-6 h-6 text-[#A3E635]" /> {t("PC-Produktion")}</h1>
        <p className="text-sm text-gray-400 mt-1">{t("Die Website-Produktion läuft lokal auf Martins PC (lokale Datenbank, ausdrücklich aktiviert). Diese Seite vermittelt nur Aufträge und Status.")}</p>
        <p className="text-xs text-gray-500 mt-1" data-testid="pc-timezone">{t("Alle Zeiten in Europe/Zurich.")}</p>
      </div>
      <section className={`rounded-2xl border p-5 ${c.cls}`} data-testid="pc-connection" data-state={d.connection.state}>
        <div className="flex flex-wrap items-center gap-3">
          <Icon className="w-5 h-5" /><span className="font-bold" data-testid="pc-connection-label">{t(c.label)}</span>
          <span className="text-xs text-gray-400" data-testid="pc-last-seen">{t("Letzte Verbindung")}: {fmtZurich(d.connection.last_seen_at, lang)}</span>
          <button onClick={load} className="sm:ml-auto text-xs text-gray-300 inline-flex items-center gap-1" data-testid="pc-refresh"><RefreshCw className="w-3.5 h-3.5" /> {t("Aktualisieren")}</button>
        </div>
        <p className="text-sm text-gray-300 mt-2">{t(c.text)}</p>
        {d.connection.test?.state !== "not_configured" && (
          <p className="text-xs text-amber-300 mt-2" data-testid="pc-test-connection">{t("TEST-Worker")}: {t(CONN[d.connection.test.state].label)} ({t("letzte Verbindung")} {fmtZurich(d.connection.test.last_seen_at, lang)}). {t("Nur für TEST-Aufträge, kein Produktions-PC.")}</p>
        )}
        {d.connection.state === "not_configured" && <p className="text-xs text-gray-400 mt-2">{t("Einrichtung: unten «Gerät koppeln» wählen und den Schritten «Einrichtung auf dem Windows-PC» folgen. Bis dahin werden keine Aufträge ausgeführt.")}</p>}
      </section>
      <Runners runners={d.runners} onPair={() => setPairing(true)} onRevoke={setRevoking} />
      <SetupGuide defaultOpen={d.connection.state === "not_configured"} />
      <section className="rounded-2xl border border-white/10 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="font-bold">{t("Aufträge")}</h2>
          <button onClick={testJob} data-testid="pc-create-test-job" className="sm:ml-auto bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-3 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5"><Play className="w-3.5 h-3.5" /> {t("TEST-Auftrag: Verbindung prüfen")}</button>
        </div>
        {d.jobs.length === 0 ? <p className="text-sm text-gray-400" data-testid="pc-no-jobs">{t("Noch keine Aufträge.")}</p> :
          <ul className="space-y-2">{d.jobs.map(j => <JobRow key={j.id} j={j} onAction={onAction} runners={d.runners} />)}</ul>}
      </section>
      <section className="rounded-2xl border border-white/10 p-5" data-testid="pc-providers">
        <h2 className="font-bold mb-2">{t("Anbieter / Werkzeuge")}</h2>
        <ul className="text-sm text-gray-400 space-y-1">{d.providers_without_adapter.map(p => (
          <li key={p} className="flex items-center gap-2"><Ban className="w-3.5 h-3.5" /> {p}: {t("nicht verfügbar (kein Adapter)")}</li>))}</ul>
        <p className="text-xs text-gray-500 mt-2">{t("Erlaubte Aktionen")}: {Object.entries(d.actions).map(([k, v]) => `${t(v.label)} [${v.environments.join("/")}]`).join(" · ")}. {t("Keine bezahlten Aufrufe, kein Versand, keine Datei-Uploads.")} <Link to="/admin/website-production" className="underline">{t("Website-Produktion (nur lokal)")}</Link></p>
      </section>
      <PairDialog open={pairing} onClose={() => setPairing(false)} onDone={load} />
      <RevokeDialog runner={revoking} onClose={() => setRevoking(null)} onDone={load} />
    </div>
  );
}
